import React, {useState} from 'react';

import {Button, CheckIcon, ModalPopup} from '@kit';

import {cls} from '../../../../components/utils';
import {navigate} from '../../../../lib/routes';
import {useCharacter} from '../character-context';
import {CharacterEditor} from '../character-editor';
import {StepContent} from './step-content';
import {isCreationComplete, useStepSummaries} from './use-step-summaries';

import css from './master.module.css';

type Props = {
	open: boolean;
	onClose(): void;
};

export const CreationMaster: React.FC<Props> = ({open, onClose}) =>
	open ? (
		<ModalPopup open onClose={onClose} header="Новый персонаж" maxWidth={800}>
			<CharacterEditor>
				<NewCharacterSteps />
			</CharacterEditor>
		</ModalPopup>
	) : null;

/** «Открыть бланк» уводит со страницы списка: окно закрывается вместе с ней, своя запись истории ему уже не мешает */
const NewCharacterSteps: React.FC = () => {
	const {sheetId} = useCharacter();
	const openSheet = () => {
		if (sheetId) navigate('sheet', {id: sheetId});
	};
	return <CreationSteps onOpenSheet={openSheet} />;
};

type CreationStepsProps = {
	onOpenSheet(): void;
};

/** шаги конструктора; перейти к листу можно, только когда создание закончено */
export const CreationSteps: React.FC<CreationStepsProps> = ({onOpenSheet}) => {
	const {sheetId} = useCharacter();
	const steps = useStepSummaries();
	const [currentIndex, setCurrentIndex] = useState(0);
	const isLastStep = currentIndex === steps.length - 1;
	const isComplete = isCreationComplete(steps);

	return (
		<>
			<div className={css.stepBar} role="tablist">
				{steps.map(({step, title, value, missingCount, errorCount}, index) => (
					<button
						key={step}
						type="button"
						role="tab"
						aria-selected={index === currentIndex}
						className={cls(
							css.stepTab,
							index === currentIndex && css.stepTabCurrent,
							errorCount > 0 && css.stepTabError,
						)}
						title={errorCount > 0 ? 'Повышение характеристики больше предела' : undefined}
						onClick={() => setCurrentIndex(index)}
					>
						<span className={css.stepTabNumber}>
							{errorCount > 0 ? '!' : value && !missingCount ? <CheckIcon /> : index + 1}
						</span>
						{title}
					</button>
				))}
			</div>

			<StepContent step={steps[currentIndex].step} />

			<div className={css.masterFooter}>
				<div className={css.masterButtons}>
					{currentIndex > 0 && <Button onClick={() => setCurrentIndex(currentIndex - 1)}>Назад</Button>}
					{!isLastStep && (
						<Button
							appearance={sheetId ? 'secondary' : 'primary'}
							onClick={() => setCurrentIndex(currentIndex + 1)}
						>
							Далее
						</Button>
					)}
					<Button
						appearance="primary"
						disabled={!sheetId || !isComplete}
						title={isComplete ? undefined : 'Сначала закончите все шаги'}
						onClick={onOpenSheet}
					>
						Открыть бланк
					</Button>
				</div>
			</div>
		</>
	);
};
